// Resolving the share-link token (?u=) for a viewer session.
//
// Pulled out of Launch.js so it can be tested directly. This is the code that
// decides whether a share link is honoured at all, and when it fails it fails
// silently: no token means no verify-narrative-token call, which means the
// visitor lands unauthenticated and sees "Could not load narratives. Your
// access token may be invalid." -- an alert that blames the token when the
// token was never sent.

// Pull {token, id} out of a search string plus a /narrative/<id> pathname.
// Returns null unless BOTH are present, since one without the other cannot
// authenticate anything.
export function readShareLink(search, pathname) {
    if (!search || !pathname) return null
    let token
    try {
        token = new URLSearchParams(search).get('u')
    } catch (e) {
        return null
    }
    if (!token) return null
    const parts = pathname.split('/narrative/')
    if (parts.length < 2) return null
    // Tolerate anything trailing the id (a slash, a nested route).
    const id = parts[1].split('/')[0]
    return id ? {token, id} : null
}

// Prefer the router's referrer state, then fall back to the live URL.
//
// The referrer is in-memory state riding on window.history.state, handed over
// by the redirect in MainRouter. It is not guaranteed to arrive: Chrome on iOS
// reached Launch with no referrer at all, which is why share links died there
// while mobile Safari was fine (0 verify-narrative-token calls from CriOS in
// the production access log, against 201s from Mobile Safari). A plain reload
// of a share link loses it the same way.
//
// window.location still carries ?u= in every one of those cases, so it is the
// dependable source. Narrative.js and NarrativeStick.js already read their
// params straight off the URL.
export function resolveShareLink(referrer, loc, useHashRouter) {
    const fromReferrer = referrer && readShareLink(referrer.search, referrer.pathname)
    if (fromReferrer) return fromReferrer
    if (!loc) return null

    // Try the plain URL first. Hash-router builds (native/local) can carry the
    // query either before or inside the '#', so offer both shapes rather than
    // guessing -- and split the fragment off, or it rides along on the token.
    const candidates = [[loc.search, loc.pathname]]
    if (useHashRouter) {
        const href = String(loc.href || '')
        const hashAt = href.indexOf('#')
        const beforeHash = hashAt > -1 ? href.slice(0, hashAt) : href
        const afterHash = hashAt > -1 ? href.slice(hashAt + 1) : ''
        candidates.push(['?' + (beforeHash.split('?')[1] || ''), loc.pathname])
        candidates.push(['?' + (afterHash.split('?')[1] || ''), afterHash.split('?')[0]])
    }

    for (let i = 0; i < candidates.length; i++) {
        const found = readShareLink(candidates[i][0], candidates[i][1])
        if (found) return found
    }
    return null
}
